/* =========================================================================
   KPI Sports — Club Shop V2.6
   Vanilla JS — quiz, product filtering, summary bar, add-to-cart.
   Supports two modes:
     1. Dynamic (config-driven) — roles loaded from data-cs-config JSON
     2. Legacy — hardcoded player/position quiz, driven by URL params
   ========================================================================= */
(function () {
  'use strict';

  var $ = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };

  function buildUrl(params) {
    var base = window.location.pathname;
    var qs = [];
    Object.keys(params).forEach(function (k) {
      if (params[k]) qs.push(encodeURIComponent(k) + '=' + encodeURIComponent(params[k]));
    });
    return base + (qs.length ? '?' + qs.join('&') : '');
  }

  function getParams() {
    var out = {};
    window.location.search.replace(/^\?/, '').split('&').forEach(function (pair) {
      if (!pair) return;
      var kv = pair.split('=');
      out[decodeURIComponent(kv[0])] = decodeURIComponent(kv[1] || '');
    });
    return out;
  }

  function capitalize(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''; }

  function formatMoney(cents) {
    var dollars = (cents / 100).toFixed(2);
    return '$' + dollars.replace(/\.00$/, '');
  }

  /* ===================== ORDER ATTRIBUTION (v2.6) =====================
     Captures the quiz selection (role / team / position) so it can be
     written onto every cart line item AND onto the order. This lets the
     selection be pulled either per-order (cart attributes) or per
     variant/line item (line item properties) when reporting on orders.
     Set on the products page; read by both add-to-cart flows below. */
  var currentSelection = null;

  function setCurrentSelection(sel) {
    currentSelection = sel;
    persistSelectionForReconcile(sel);
  }

  // Shared with club-shop-reconcile.js (loaded site-wide from theme.liquid):
  // the latest selection plus this club's product ids, so adds that bypass
  // this page (Tepo product-page widget, direct product pages) can still be
  // stamped with attribution before checkout.
  var RECONCILE_KEY = 'cs-selection';

  function collectClubProductIds() {
    var ids = [];
    $$('[data-cs-product-id]').forEach(function (el) {
      var id = parseInt(el.getAttribute('data-cs-product-id'), 10);
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function persistSelectionForReconcile(sel) {
    if (!sel) return;
    storeSet(RECONCILE_KEY, {
      playerName: sel.playerName || '',
      firstName: sel.firstName || '',
      lastName: sel.lastName || '',
      role: sel.role || '',
      team: sel.team || '',
      position: sel.position || '',
      collectionUrl: (config && config.collectionUrl) || window.location.pathname,
      products: collectClubProductIds()
    });
  }

  // Quiz state is kept in localStorage (not sessionStorage) so it survives
  // new tabs, shared links and return visits — losing it silently dropped
  // the player name off line items. Entries expire after STORE_TTL_MS and
  // are cleared on "Start over". sessionStorage is still read as a fallback
  // for values written by older builds mid-session.
  var STORE_TTL_MS = 24 * 60 * 60 * 1000;

  function storeGet(key) {
    try {
      var raw = localStorage.getItem(key);
      if (raw) {
        var obj = JSON.parse(raw);
        if (obj && obj.ts && (Date.now() - obj.ts) < STORE_TTL_MS) return obj.v;
        localStorage.removeItem(key);
      }
    } catch (e) {}
    try { return sessionStorage.getItem(key); } catch (e2) { return null; }
  }
  function storeSet(key, v) {
    try { localStorage.setItem(key, JSON.stringify({ v: v, ts: Date.now() })); } catch (e) {}
  }
  function storeRemove(key) {
    try { localStorage.removeItem(key); } catch (e) {}
    try { sessionStorage.removeItem(key); } catch (e2) {}
  }

  // Player name is collected in the quiz but never put in the URL (privacy).
  // Scoped per collection so one club's name can't bleed into another.
  // Stored SPLIT as { first, last } — never split after entry (double-
  // barrelled surnames, middle names etc. make any auto-split wrong). The
  // combined display name is derived by joinPlayerName. Values written by
  // older builds are a plain string; kept as `full` so an in-flight session
  // still gets a Player property (but no split fields — can't be derived).
  function playerNameKey() {
    var scope = (config && config.collectionUrl) ? config.collectionUrl : window.location.pathname;
    return 'cs-player-name:' + scope;
  }
  function getStoredPlayerParts() {
    var v = storeGet(playerNameKey());
    if (!v) return { first: '', last: '', full: '' };
    if (typeof v === 'string') return { first: '', last: '', full: v };
    return { first: v.first || '', last: v.last || '', full: v.full || '' };
  }
  function joinPlayerName(parts) {
    if (!parts) return '';
    var full = ((parts.first || '') + ' ' + (parts.last || '')).replace(/\s+/g, ' ').trim();
    return full || parts.full || '';
  }
  function setStoredPlayerParts(parts) {
    storeSet(playerNameKey(), {
      first: (parts && parts.first) || '',
      last: (parts && parts.last) || '',
      full: joinPlayerName(parts)
    });
  }
  function clearStoredPlayerName() {
    storeRemove(playerNameKey());
  }

  // Chosen sizes (per size category) — stored as a map, scoped per
  // collection. Not sensitive, but kept out of the URL for tidiness.
  function sizesKey() {
    var scope = (config && config.collectionUrl) ? config.collectionUrl : window.location.pathname;
    return 'cs-sizes:' + scope;
  }
  function getStoredSizes() {
    var v = storeGet(sizesKey());
    if (!v) return {};
    if (typeof v === 'string') {
      try { return JSON.parse(v) || {}; } catch (e) { return {}; }
    }
    return v;
  }
  function setStoredSizes(obj) {
    storeSet(sizesKey(), obj || {});
  }
  function clearStoredSizes() {
    storeRemove(sizesKey());
  }

  // Line-item properties. Underscore-prefixed keys are stored on the order
  // but hidden from the customer's cart display; 'Player' stays visible.
  // Returns null if empty.
  function buildLineItemProperties() {
    if (!currentSelection) return null;
    var props = {};
    // 'Player' stays as the combined display name (warehouse/reporting read
    // it); the split fields ride alongside, hidden, for Majestri.
    if (currentSelection.playerName) props['Player'] = currentSelection.playerName;
    if (currentSelection.firstName) props['_First Name'] = currentSelection.firstName;
    if (currentSelection.lastName) props['_Last Name'] = currentSelection.lastName;
    if (currentSelection.role) props['_Role'] = currentSelection.role;
    if (currentSelection.team) props['_Team'] = currentSelection.team;
    if (currentSelection.position) props['_Position'] = currentSelection.position;
    return Object.keys(props).length ? props : null;
  }

  /* ===================== PERSONALISATION (v2.6) =========================
     Per-product customisation captured inline on the card. Each box tag on the
     product (name-box / ma-name-box / initial-box / number-dropdown) renders an
     input carrying the property key Tepo uses (Name confirmed on live orders;
     Initials / Number for the others). The value is written to THAT product's
     line item, so it reads off the Shopify order against the right item.
     Required fields block the add so nothing ships blank. */
  var PERSONALISATION_ENABLED = true;

  function personalisationInputs(productEl) {
    return productEl ? $$('[data-cs-personalise]', productEl) : [];
  }

  // Build personalisation properties from a product card's inline fields.
  function buildPersonalisationProperties(productEl) {
    if (!PERSONALISATION_ENABLED) return null;
    var props = {};
    personalisationInputs(productEl).forEach(function (inp) {
      var key = inp.getAttribute('data-cs-personalise-key');
      var val = (inp.value || '').trim();
      if (key && val) props[key] = val;
    });
    return Object.keys(props).length ? props : null;
  }

  // First required-but-empty personalisation input on a card, or null if OK.
  function firstMissingPersonalisation(productEl) {
    var missing = null;
    personalisationInputs(productEl).forEach(function (inp) {
      if (missing) return;
      if (inp.hasAttribute('data-cs-personalise-required') && !(inp.value || '').trim()) {
        missing = inp;
      }
    });
    return missing;
  }

  // Highlight a required personalisation field that was left empty.
  function flagMissingPersonalisation(input) {
    if (!input) return;
    input.classList.add('cs-pers-input-error');
    try { input.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) {}
    try { input.focus({ preventScroll: true }); } catch (e2) { try { input.focus(); } catch (e3) {} }
  }

  // Disable a card's Add button while any required personalisation field on it
  // is empty, so the item can't be added without (e.g.) a name on it.
  function syncCardAddState(productEl) {
    if (!productEl) return;
    var inputs = personalisationInputs(productEl);
    if (!inputs.length) return;
    var btn = $('[data-cs-add-btn]', productEl);
    if (!btn) return;
    var blocked = !!firstMissingPersonalisation(productEl);
    btn.disabled = blocked;
    btn.classList.toggle('cs-add-btn-blocked', blocked);
  }

  // Clear the error state and refresh the Add button as the parent types.
  document.addEventListener('input', function (e) {
    var inp = e.target;
    if (inp && inp.hasAttribute && inp.hasAttribute('data-cs-personalise')) {
      inp.classList.remove('cs-pers-input-error');
      syncCardAddState(inp.closest('[data-cs-product]'));
    }
  });

  // Merge a product's personalisation into a base properties object. No-op (and
  // returns the original object untouched) while personalisation is disabled.
  function mergePersonalisation(baseProps, productEl) {
    if (!PERSONALISATION_ENABLED) return baseProps;
    var extra = buildPersonalisationProperties(productEl);
    if (!extra) return baseProps;
    var merged = {};
    if (baseProps) { Object.keys(baseProps).forEach(function (k) { merged[k] = baseProps[k]; }); }
    Object.keys(extra).forEach(function (k) { merged[k] = extra[k]; });
    return merged;
  }

  // Order-level note attributes — a summary of the latest selection.
  function updateOrderAttributes() {
    if (!currentSelection) return;
    var attrs = {};
    if (currentSelection.playerName) attrs['Player Name'] = currentSelection.playerName;
    if (currentSelection.firstName) attrs['First Name'] = currentSelection.firstName;
    if (currentSelection.lastName) attrs['Last Name'] = currentSelection.lastName;
    if (currentSelection.role) attrs['Role'] = currentSelection.role;
    if (currentSelection.team) attrs['Team'] = currentSelection.team;
    if (currentSelection.position) attrs['Position'] = currentSelection.position;
    var sizes = currentSelection.sizes || {};
    Object.keys(sizes).forEach(function (cat) {
      if (sizes[cat]) attrs[cat + ' Size'] = sizes[cat];
    });
    if (!Object.keys(attrs).length) return;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attrs }),
      credentials: 'same-origin'
    }).catch(function () {});
  }

  var TICK_SVG = '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke-width="3"><path d="M5 13l4 4L19 7"/></svg>';
  var EDIT_SVG = '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>';
  var CHECK_SVG = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#fff" stroke-width="3"><path d="M5 13l4 4L19 7"/></svg>';
  var CART_SVG = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.4"><circle cx="9" cy="20" r="1.4"/><circle cx="18" cy="20" r="1.4"/><path d="M2 3h3l2.5 13h11L21 7H6"/></svg>';

  /* ===================== CONFIG ===================== */
  var config = null;
  var configEl = document.querySelector('[data-cs-config]');
  if (configEl) {
    try { config = JSON.parse(configEl.textContent); } catch (e) { config = null; }
  }

  function slugify(s) {
    return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  // Defensive: a role must have a usable handle for selection/URLs to work.
  // If the metaobject handle didn't render, fall back to a slug of the label.
  if (config && config.roles) {
    config.roles.forEach(function (r) {
      if (!r.handle) r.handle = slugify(r.label);
    });
  }

  function findRole(handle) {
    if (!config) return null;
    for (var i = 0; i < config.roles.length; i++) {
      if (config.roles[i].handle === handle) return config.roles[i];
    }
    return null;
  }

  function roleNeedsPositionStep(role) {
    return role.askPosition && role.positions && role.positions.length > 1;
  }

  function roleEffectivePosition(role, selectedPosition) {
    if (selectedPosition) return selectedPosition;
    if (role.positions && role.positions.length === 1) return role.positions[0].toLowerCase();
    if (role.defaultPosition) return role.defaultPosition.toLowerCase();
    return '';
  }

  /* ===================== TEAM SCANNING ===================== */
  function scanTeamsFromPool(rolePositions) {
    var pool = $('[data-cs-product-pool]');
    if (!pool) return [];
    var products = $$('[data-cs-product]', pool);
    var teamSet = {};
    var validPositions = (rolePositions || []).map(function (p) { return p.toLowerCase(); });

    products.forEach(function (el) {
      if (validPositions.length > 0) {
        var prodPositions = el.getAttribute('data-cs-product-positions') || '';
        if (prodPositions) {
          var posList = prodPositions.split('|');
          var match = false;
          for (var i = 0; i < validPositions.length; i++) {
            if (posList.indexOf(validPositions[i]) !== -1) { match = true; break; }
          }
          if (!match) return;
        }
      }

      var prodTeams = el.getAttribute('data-cs-product-teams') || '';
      if (prodTeams) {
        prodTeams.split('|').forEach(function (t) {
          var trimmed = t.trim();
          if (trimmed) teamSet[trimmed] = true;
        });
      }
    });

    return Object.keys(teamSet).sort();
  }

  function teamDisplayName(slug) {
    // Convert "u15-boys" or "u15 boys" to "U15 Boys"
    return slug.replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  /* ===================== SIZE SCANNING / MATCHING (v2.6) =====================
     Products carry a free-text size category (data-cs-product-size-category).
     The quiz asks one size per category that actually appears in the pool,
     offering only sizes that exist on those products. On the products page the
     chosen size is auto-applied per product: exact match → else round up to the
     nearest available → else left for manual selection (never a wrong size). */

  // Rough ordinal scale spanning common kit sizes, used only for round-up.
  var SIZE_ORDER = [
    '3-4', '4-5', '5-6', '6-7', '7-8', '8-9', '9-10', '10-11', '11-12', '12-13', '13-14', '14-15',
    '2XS', 'XXS', 'YXS', 'XS', 'YS', 'S', 'YM', 'M', 'YL', 'L', 'YXL', 'XL', '2XL', 'XXL', '3XL', 'XXXL', '4XL'
  ];

  function normalizeSize(s) {
    return (s || '')
      .toUpperCase()
      .replace(/\bYOUTH\b/g, 'Y')
      .replace(/\bADULT\b/g, '')
      .replace(/\bYEARS?\b/g, '')
      .replace(/\bYRS?\b/g, '')
      .replace(/[\s_]+/g, '')
      .trim();
  }

  function sizeRank(s) {
    return SIZE_ORDER.indexOf(normalizeSize(s));
  }

  // Read the selectable sizes off a product's card (single-option size chips).
  // Returns [{ size, variantId, available }]. Multi-option products (size +
  // colour) are skipped — they keep manual selection.
  function getCardSizes(productEl) {
    var out = [];
    $$('[data-cs-size]', productEl).forEach(function (chip) {
      out.push({
        size: (chip.textContent || '').trim(),
        variantId: chip.getAttribute('data-variant-id'),
        available: !chip.disabled
      });
    });
    return out;
  }

  // Scan the whole pool for the size categories present and the sizes available
  // in each. Returns [{ category, sizes: [orderedSizeStrings] }].
  function scanSizeCategoriesFromPool() {
    var pool = $('[data-cs-product-pool]');
    if (!pool) return [];
    var map = {}; // category -> { sizeKey -> displaySize }
    $$('[data-cs-product]', pool).forEach(function (el) {
      var cat = (el.getAttribute('data-cs-product-size-category') || '').trim();
      if (!cat) return;
      if (el.getAttribute('data-cs-product-tepo') === 'true') return;
      var sizes = getCardSizes(el);
      if (!sizes.length) return;
      if (!map[cat]) map[cat] = {};
      sizes.forEach(function (s) {
        if (s.size) map[cat][normalizeSize(s.size)] = s.size;
      });
    });
    return Object.keys(map).map(function (cat) {
      var sizes = Object.keys(map[cat]).map(function (k) { return map[cat][k]; });
      sizes.sort(function (a, b) {
        var ra = sizeRank(a), rb = sizeRank(b);
        if (ra === -1 && rb === -1) return a.localeCompare(b);
        if (ra === -1) return 1;
        if (rb === -1) return -1;
        return ra - rb;
      });
      return { category: cat, sizes: sizes };
    });
  }

  /* ===================== PAGE STATE ===================== */
  function initPage() {
    var section = $('[data-cs-section]');
    if (!section) return;

    if (config) {
      initPageDynamic(section);
    } else {
      initPageLegacy(section);
    }
  }

  function initPageLegacy(section) {
    var params = getParams();
    var player = params.player || '';
    var position = params.position || 'outfield';
    var hasSelection = !!player;

    var quizWrap = $('[data-cs-quiz-wrap]', section);
    var productsWrap = $('[data-cs-products-wrap]', section);

    if (!quizWrap || !productsWrap) return;

    if (!hasSelection) {
      quizWrap.style.display = '';
      productsWrap.style.display = 'none';
      return;
    }

    quizWrap.style.display = 'none';
    productsWrap.style.display = '';

    var supportersUrl = section.getAttribute('data-cs-supporters-url');
    if (player === 'supporter' && supportersUrl) {
      window.location.href = supportersUrl;
      return;
    }

    updateSummaryBarLegacy(section, player, position);
    distributeProductsLegacy(section, player, position);
  }

  function initPageDynamic(section) {
    var params = getParams();
    var roleHandle = params.role;

    var quizWrap = $('[data-cs-quiz-wrap]', section);
    var productsWrap = $('[data-cs-products-wrap]', section);
    if (!quizWrap || !productsWrap) return;

    if (!roleHandle) {
      quizWrap.style.display = '';
      productsWrap.style.display = 'none';
      return;
    }

    var role = findRole(roleHandle);
    if (!role) {
      quizWrap.style.display = '';
      productsWrap.style.display = 'none';
      return;
    }

    if (role.redirectUrl) {
      window.location.href = role.redirectUrl;
      return;
    }

    quizWrap.style.display = 'none';
    productsWrap.style.display = '';

    var position = params.position || roleEffectivePosition(role, '');
    var player = role.playerType || '';
    var team = params.team || '';

    var nameParts = config.collectPlayerName ? getStoredPlayerParts() : null;
    setCurrentSelection({
      role: role.label || '',
      team: team ? teamDisplayName(team) : '',
      position: position ? capitalize(position) : '',
      playerName: nameParts ? joinPlayerName(nameParts) : '',
      firstName: nameParts ? nameParts.first : '',
      lastName: nameParts ? nameParts.last : '',
      sizes: getStoredSizes()
    });

    updateDynamicSummaryBar(section, role, position, team);
    distributeProductsDynamic(section, role, position, player, team);
  }

  /* ===================== LEGACY SUMMARY BAR ===================== */
  function updateSummaryBarLegacy(section, player, position) {
    var label = $('[data-cs-sum-player-label]', section);
    if (label) label.textContent = capitalize(player) + ' player';

    var playerVal = $('[data-cs-chip-player-val]', section);
    if (playerVal) playerVal.textContent = capitalize(player) + ' player';

    var posVal = $('[data-cs-chip-position-val]', section);
    if (posVal) posVal.textContent = capitalize(position);

    $$('[data-cs-pop="player"] [data-cs-pop-val]', section).forEach(function (opt) {
      opt.classList.toggle('sel', opt.getAttribute('data-cs-pop-val') === player);
    });
    $$('[data-cs-pop="position"] [data-cs-pop-val]', section).forEach(function (opt) {
      opt.classList.toggle('sel', opt.getAttribute('data-cs-pop-val') === position);
    });

    var posChip = $('[data-cs-chip-toggle="position"]', section);
    if (posChip) posChip.classList.toggle('cs-chip-gk', position === 'goalkeeper');
  }

  /* ===================== DYNAMIC SUMMARY BAR ===================== */
  function updateDynamicSummaryBar(section, role, position, team) {
    var sumHi = $('.cs-sum-hi', section);
    var sumLabel = $('[data-cs-sum-player-label]', section);
    var name = currentSelection && currentSelection.playerName;
    if (name) {
      if (sumHi) sumHi.textContent = 'Shopping for';
      if (sumLabel) sumLabel.textContent = name;
    } else {
      if (sumHi) sumHi.textContent = 'Shopping as';
      if (sumLabel) sumLabel.textContent = role.label;
    }

    var container = $('[data-cs-chips-container]', section);
    if (!container) return;
    container.innerHTML = '';

    buildRoleChip(container, role);

    if (position && (roleNeedsPositionStep(role) || role.defaultPosition)) {
      buildPositionChip(container, role, position);
    }

    if (team) {
      buildTeamChip(container, role, team);
    }
  }

  function buildRoleChip(container, activeRole) {
    var wrap = document.createElement('div');
    wrap.className = 'cs-chip-wrap';
    wrap.setAttribute('data-cs-chip-wrap', '');

    var btn = document.createElement('button');
    btn.className = 'cs-chip';
    btn.setAttribute('data-cs-chip-toggle', 'role');
    btn.innerHTML = '<span class="cs-chip-key">Shopping as</span>' +
      '<span class="cs-chip-val">' + escHtml(activeRole.label) + '</span>' +
      '<span class="cs-chip-edit">' + EDIT_SVG + '</span>';
    wrap.appendChild(btn);

    var pop = document.createElement('div');
    pop.className = 'cs-pop';
    pop.setAttribute('data-cs-pop', 'role');
    pop.style.display = 'none';

    config.roles.forEach(function (r) {
      if (r.redirectUrl) return;
      var opt = document.createElement('div');
      opt.className = 'cs-pop-opt' + (r.handle === activeRole.handle ? ' sel' : '');
      opt.setAttribute('data-cs-pop-val', r.handle);
      opt.setAttribute('data-cs-pop-type', 'role');
      var desc = r.playerType ? capitalize(r.playerType) + ' player' : r.label;
      opt.innerHTML = '<span class="cs-pop-dot"></span><span>' + escHtml(r.label) + '<small>' + escHtml(desc) + '</small></span>';
      pop.appendChild(opt);
    });
    wrap.appendChild(pop);
    container.appendChild(wrap);
  }

  function buildPositionChip(container, role, currentPosition) {
    if (!role.positions || role.positions.length < 2) return;

    var wrap = document.createElement('div');
    wrap.className = 'cs-chip-wrap';
    wrap.setAttribute('data-cs-chip-wrap', '');

    var btn = document.createElement('button');
    btn.className = 'cs-chip';
    btn.setAttribute('data-cs-chip-toggle', 'position');
    btn.innerHTML = '<span class="cs-chip-key">Position</span>' +
      '<span class="cs-chip-val">' + escHtml(capitalize(currentPosition)) + '</span>' +
      '<span class="cs-chip-edit">' + EDIT_SVG + '</span>';
    wrap.appendChild(btn);

    var pop = document.createElement('div');
    pop.className = 'cs-pop';
    pop.setAttribute('data-cs-pop', 'position');
    pop.style.display = 'none';

    role.positions.forEach(function (p) {
      var val = p.toLowerCase();
      var opt = document.createElement('div');
      opt.className = 'cs-pop-opt' + (val === currentPosition.toLowerCase() ? ' sel' : '');
      opt.setAttribute('data-cs-pop-val', val);
      opt.setAttribute('data-cs-pop-type', 'position');
      opt.innerHTML = '<span class="cs-pop-dot"></span><span>' + escHtml(p) + '</span>';
      pop.appendChild(opt);
    });
    wrap.appendChild(pop);
    container.appendChild(wrap);
  }

  function buildTeamChip(container, role, currentTeam) {
    var teams = scanTeamsFromPool(role.positions || []);
    if (teams.length === 0) return;

    var wrap = document.createElement('div');
    wrap.className = 'cs-chip-wrap';
    wrap.setAttribute('data-cs-chip-wrap', '');

    var btn = document.createElement('button');
    btn.className = 'cs-chip';
    btn.setAttribute('data-cs-chip-toggle', 'team');
    btn.innerHTML = '<span class="cs-chip-key">Team</span>' +
      '<span class="cs-chip-val">' + escHtml(teamDisplayName(currentTeam)) + '</span>' +
      '<span class="cs-chip-edit">' + EDIT_SVG + '</span>';
    wrap.appendChild(btn);

    var pop = document.createElement('div');
    pop.className = 'cs-pop';
    pop.setAttribute('data-cs-pop', 'team');
    pop.style.display = 'none';

    teams.forEach(function (t) {
      var opt = document.createElement('div');
      opt.className = 'cs-pop-opt' + (t === currentTeam.toLowerCase() ? ' sel' : '');
      opt.setAttribute('data-cs-pop-val', t);
      opt.setAttribute('data-cs-pop-type', 'team');
      opt.innerHTML = '<span class="cs-pop-dot"></span><span>' + escHtml(teamDisplayName(t)) + '</span>';
      pop.appendChild(opt);
    });
    wrap.appendChild(pop);
    container.appendChild(wrap);
  }

  /* ===================== LEGACY PRODUCT DISTRIBUTION ===================== */
  function distributeProductsLegacy(section, player, position) {
    var pool = $('[data-cs-product-pool]', section);
    var compGrid = $('[data-cs-compulsory-grid]', section);
    var optGrid = $('[data-cs-optional-grid]', section);
    var compSection = $('[data-cs-compulsory-section]', section);
    var optSection = $('[data-cs-optional-section]', section);
    var emptyState = $('[data-cs-empty-state]', section);

    if (!pool || !compGrid || !optGrid) return;

    var products = $$('[data-cs-product]', pool);
    var compCount = 0;
    var compTotal = 0;
    var optCount = 0;

    products.forEach(function (el) {
      var prodPos = el.getAttribute('data-cs-product-position') || '';
      var req = el.getAttribute('data-cs-product-requirement') || 'always_optional';
      var price = parseInt(el.getAttribute('data-cs-product-price') || '0', 10);

      var posVisible = true;
      if (prodPos === 'goalkeeper' && position !== 'goalkeeper') posVisible = false;
      if (prodPos === 'outfield' && position === 'goalkeeper') posVisible = false;

      if (!posVisible) return;

      var isCompulsory = false;
      if (req === 'always_required') isCompulsory = true;
      else if (req === 'new_only_required' && player === 'new') isCompulsory = true;

      var card = $('[data-cs-card]', el);
      if (card) {
        var wrapper = el.cloneNode(true);
        var clonedCard = $('[data-cs-card]', wrapper);

        if (isCompulsory) {
          wrapper.setAttribute('data-cs-bucket', 'compulsory');
          if (clonedCard) clonedCard.setAttribute('data-cs-bucket', 'compulsory');
          if (prodPos === 'goalkeeper') wrapper.setAttribute('data-cs-gk-swap', '');
          compGrid.appendChild(wrapper);
          compCount++;
          compTotal += price;
        } else {
          wrapper.setAttribute('data-cs-bucket', 'optional');
          if (clonedCard) clonedCard.setAttribute('data-cs-bucket', 'optional');
          if (prodPos === 'goalkeeper') wrapper.setAttribute('data-cs-gk-swap', '');
          optGrid.appendChild(wrapper);
          optCount++;
        }
      }
    });

    if (compSection) {
      compSection.style.display = compCount > 0 ? '' : 'none';
      var countEl = $('[data-cs-compulsory-count]', section);
      if (countEl) countEl.textContent = compCount + ' item' + (compCount !== 1 ? 's' : '');
      var descEl = $('[data-cs-compulsory-desc]', section);
      if (descEl) {
        descEl.textContent = player === 'new'
          ? 'Every new player needs these to take the field this season.'
          : 'Match-day essentials — these stay required for returning players.';
      }
    }

    if (optSection) {
      optSection.style.display = optCount > 0 ? '' : 'none';
      var optCountEl = $('[data-cs-optional-count]', section);
      if (optCountEl) optCountEl.textContent = optCount + ' item' + (optCount !== 1 ? 's' : '');
      var optDescEl = $('[data-cs-optional-desc]', section);
      if (optDescEl) {
        optDescEl.textContent = player === 'returning'
          ? 'Includes bags, jackets & polos — already own yours? Skip them.'
          : 'Extras to round out the kit bag. Add what you like.';
      }
    }

    if (emptyState) {
      emptyState.style.display = (compCount + optCount === 0) ? '' : 'none';
    }
  }

  /* ===================== DYNAMIC PRODUCT DISTRIBUTION ===================== */
  function distributeProductsDynamic(section, role, position, player, team) {
    var pool = $('[data-cs-product-pool]', section);
    var compGrid = $('[data-cs-compulsory-grid]', section);
    var optGrid = $('[data-cs-optional-grid]', section);
    var compSection = $('[data-cs-compulsory-section]', section);
    var optSection = $('[data-cs-optional-section]', section);
    var emptyState = $('[data-cs-empty-state]', section);

    if (!pool || !compGrid || !optGrid) return;

    var products = $$('[data-cs-product]', pool);
    var compCount = 0;
    var optCount = 0;
    var posLower = position ? position.toLowerCase() : '';
    var teamParam = team ? team.toLowerCase() : '';

    products.forEach(function (el) {
      var prodPositions = el.getAttribute('data-cs-product-positions') || '';
      var req = el.getAttribute('data-cs-product-requirement') || 'always_optional';

      var posVisible = true;
      if (prodPositions && posLower) {
        var posList = prodPositions.split('|');
        posVisible = posList.indexOf(posLower) !== -1;
      }
      if (!posVisible) return;

      // Team filtering: if product has teams and team param set, filter
      var prodTeams = el.getAttribute('data-cs-product-teams') || '';
      if (prodTeams && teamParam) {
        var teamList = prodTeams.split('|');
        if (teamList.indexOf(teamParam) === -1) return; // hide
      }
      // blank teams = show for all teams

      var isCompulsory = false;
      if (!role.allOptional) {
        if (req === 'always_required') isCompulsory = true;
        else if (req === 'new_only_required' && player === 'new') isCompulsory = true;
      }

      var isTepo = el.getAttribute('data-cs-product-tepo') === 'true';

      var card = $('[data-cs-card]', el);
      if (card) {
        var wrapper = el.cloneNode(true);
        var clonedCard = $('[data-cs-card]', wrapper);
        var bucket = isCompulsory ? 'compulsory' : 'optional';

        wrapper.setAttribute('data-cs-bucket', bucket);
        if (clonedCard) clonedCard.setAttribute('data-cs-bucket', bucket);

        // Add checkbox overlay for bulk-add (not for Tepo items)
        if (!isTepo && clonedCard) {
          var checkEl = document.createElement('label');
          checkEl.className = 'cs-card-check' + (isCompulsory ? ' checked' : '');
          checkEl.setAttribute('data-cs-card-check', '');
          checkEl.innerHTML = '<span class="cs-card-check-box">' + CHECK_SVG + '</span>';
          // Store variant info on the wrapper for bulk add
          var variantInput = $('[data-cs-variant-id]', wrapper);
          if (variantInput) {
            wrapper.setAttribute('data-cs-variant-id', variantInput.value);
          }
          wrapper.setAttribute('data-cs-checked', isCompulsory ? 'true' : 'false');

          var mediaEl = $('.cs-card-media', clonedCard);
          if (mediaEl) {
            mediaEl.appendChild(checkEl);
          }
        }

        if (isCompulsory) {
          compGrid.appendChild(wrapper);
          compCount++;
        } else {
          optGrid.appendChild(wrapper);
          optCount++;
        }
      }
    });

    if (compSection) {
      compSection.style.display = compCount > 0 ? '' : 'none';
      var countEl = $('[data-cs-compulsory-count]', section);
      if (countEl) countEl.textContent = compCount + ' item' + (compCount !== 1 ? 's' : '');
      var descEl = $('[data-cs-compulsory-desc]', section);
      if (descEl) {
        if (player === 'new') {
          descEl.textContent = 'Every new player needs these to take the field this season.';
        } else if (player === 'returning') {
          descEl.textContent = 'Match-day essentials — these stay required for returning players.';
        } else {
          descEl.textContent = 'These items are required for the season.';
        }
      }
    }

    if (optSection) {
      optSection.style.display = optCount > 0 ? '' : 'none';
      var optCountEl = $('[data-cs-optional-count]', section);
      if (optCountEl) optCountEl.textContent = optCount + ' item' + (optCount !== 1 ? 's' : '');
      var optDescEl = $('[data-cs-optional-desc]', section);
      if (optDescEl) {
        if (role.allOptional) {
          optDescEl.textContent = 'All items are optional — pick what you need.';
        } else if (player === 'returning') {
          optDescEl.textContent = 'Includes bags, jackets & polos — already own yours? Skip them.';
        } else {
          optDescEl.textContent = 'Extras to round out the kit bag. Add what you like.';
        }
      }
    }

    if (role.allOptional && optSection) {
      var optTitle = $('[data-cs-optional-section] h2', section);
      if (optTitle) optTitle.textContent = 'Available kit';
      var optPill = $('[data-cs-optional-section] .cs-pillmark-opt', section);
      if (optPill) optPill.textContent = 'Optional';
    }

    if (emptyState) {
      emptyState.style.display = (compCount + optCount === 0) ? '' : 'none';
    }

    // Auto-apply the player's chosen sizes to the visible products.
    if (config && config.collectSizes) autoApplySizes(section);

    // Gate the Add button on any product that needs a personalisation value.
    $$('[data-cs-product]', section).forEach(function (el) {
      if (el.closest('[data-cs-product-pool]')) return;
      syncCardAddState(el);
    });

    // Initialise bulk-add bar if any checkable products exist
    if (compCount + optCount > 0) {
      initBulkAdd(section);
    }
  }

  /* ===================== SIZE AUTO-APPLY (v2.6) =====================
     For each visible product, look up the chosen size for its category and
     pre-select the matching variant. Exact match → else round up to nearest
     available → else leave untouched (manual). Never selects a wrong size. */
  function autoApplySizes(section) {
    var sizes = getStoredSizes();
    if (!sizes || !Object.keys(sizes).length) return;

    $$('[data-cs-product]', section).forEach(function (el) {
      if (el.closest('[data-cs-product-pool]')) return; // skip the hidden pool
      var cat = (el.getAttribute('data-cs-product-size-category') || '').trim();
      if (!cat || !sizes[cat]) return;

      var chips = $$('[data-cs-size]', el);
      if (!chips.length) return; // multi-option / no size chips → manual

      var chosen = sizes[cat];
      var match = pickSizeChip(chips, chosen);
      if (!match) return;

      chips.forEach(function (c) { c.classList.remove('sel'); });
      match.classList.add('sel');
      var variantId = match.getAttribute('data-variant-id');
      var hidden = $('[data-cs-variant-id]', el);
      if (hidden && variantId) hidden.value = variantId;
    });
  }

  // Choose the best available chip for the requested size.
  function pickSizeChip(chips, requested) {
    var want = normalizeSize(requested);
    var available = chips.filter(function (c) { return !c.disabled; });
    if (!available.length) return null;

    // Exact match.
    for (var i = 0; i < available.length; i++) {
      if (normalizeSize(available[i].textContent) === want) return available[i];
    }
    // Round up: smallest available size larger than requested.
    var wantRank = sizeRank(requested);
    if (wantRank === -1) return null;
    var best = null, bestRank = Infinity;
    available.forEach(function (c) {
      var r = sizeRank(c.textContent);
      if (r > wantRank && r < bestRank) { bestRank = r; best = c; }
    });
    return best; // may be null → left for manual selection
  }

  /* ===================== BULK ADD TO CART ===================== */
  function initBulkAdd(section) {
    // Remove any existing bulk bar
    var existing = $('[data-cs-bulk-bar]');
    if (existing) existing.parentNode.removeChild(existing);

    var checkables = $$('[data-cs-card-check]', section);
    if (!checkables.length) return;

    // Create the sticky bulk-add bar
    var bar = document.createElement('div');
    bar.className = 'cs-bulk-bar';
    bar.setAttribute('data-cs-bulk-bar', '');
    bar.innerHTML =
      '<div class="cs-wrap">' +
        '<div class="cs-bulk-info">' +
          CART_SVG +
          ' <span data-cs-bulk-count>0</span> items selected' +
          ' <span class="cs-bulk-total" data-cs-bulk-total></span>' +
        '</div>' +
        '<button class="cs-btn cs-btn-primary cs-bulk-btn" data-cs-bulk-btn disabled>' +
          'Add <span data-cs-bulk-btn-count>0</span> items to cart' +
        '</button>' +
      '</div>';
    section.closest('[data-cs-section]').appendChild(bar);

    updateBulkBar(section);

    // Checkbox click handler (delegated)
    section.addEventListener('click', function (e) {
      var check = e.target.closest('[data-cs-card-check]');
      if (!check) return;
      e.preventDefault();
      e.stopPropagation();

      var wrapper = check.closest('[data-cs-product]');
      if (!wrapper) return;

      var isChecked = wrapper.getAttribute('data-cs-checked') === 'true';
      var newState = !isChecked;

      wrapper.setAttribute('data-cs-checked', newState ? 'true' : 'false');
      check.classList.toggle('checked', newState);

      updateBulkBar(section);
    });

    // Bulk add button
    bar.addEventListener('click', function (e) {
      if (!e.target.closest('[data-cs-bulk-btn]')) return;
      var bulkBtn = $('[data-cs-bulk-btn]', bar);
      if (!bulkBtn || bulkBtn.disabled) return;

      var items = getCheckedItems(section);
      if (!items.length) return;

      // Block the whole bulk add if any selected item needs a personalisation
      // value — highlight the first offender so nothing ships blank.
      for (var m = 0; m < items.length; m++) {
        var miss = firstMissingPersonalisation(items[m].el);
        if (miss) { flagMissingPersonalisation(miss); return; }
      }

      bulkBtn.disabled = true;
      addItemsSequentially(items, 0, items.length, bulkBtn, section);
    });
  }

  function getCheckedItems(section) {
    var items = [];
    $$('[data-cs-product][data-cs-checked="true"]', section).forEach(function (el) {
      var variantId = el.getAttribute('data-cs-variant-id');
      // Also check the current selected variant (user may have changed size)
      var variantInput = $('[data-cs-variant-id]', el);
      if (variantInput && variantInput.value) {
        variantId = variantInput.value;
      }
      var title = el.getAttribute('data-cs-product-title') || 'Item';
      if (variantId) {
        items.push({ id: parseInt(variantId, 10), title: title, el: el });
      }
    });
    return items;
  }

  function updateBulkBar(section) {
    var bar = $('[data-cs-bulk-bar]');
    if (!bar) return;

    var items = getCheckedItems(section);
    var count = items.length;

    var countEl = $('[data-cs-bulk-count]', bar);
    if (countEl) countEl.textContent = count;

    var btnCountEl = $('[data-cs-bulk-btn-count]', bar);
    if (btnCountEl) btnCountEl.textContent = count;

    var bulkBtn = $('[data-cs-bulk-btn]', bar);
    if (bulkBtn) bulkBtn.disabled = count === 0;

    // Calculate total price from checked products
    var total = 0;
    $$('[data-cs-product][data-cs-checked="true"]', section).forEach(function (el) {
      var price = parseInt(el.getAttribute('data-cs-product-price') || '0', 10);
      total += price;
    });

    var totalEl = $('[data-cs-bulk-total]', bar);
    if (totalEl) {
      totalEl.textContent = count > 0 ? '(' + formatMoney(total) + ')' : '';
    }

    // Show/hide the bar
    bar.style.display = count > 0 ? '' : 'none';
  }

  // Uncheck a card (used after a partial bulk add so a retry click only
  // re-adds what actually failed).
  function setCardUnchecked(el) {
    if (!el) return;
    el.setAttribute('data-cs-checked', 'false');
    var check = $('[data-cs-card-check]', el);
    if (check) check.classList.remove('checked');
  }

  function addItemsSequentially(items, idx, total, bulkBtn, section, retried) {
    if (idx >= items.length) {
      var failed = items.filter(function (it) { return it.failed; });
      if (failed.length) {
        // Leave only the failed items checked so the next click on the bulk
        // button retries just those; never pretend everything was added.
        items.forEach(function (it) { if (!it.failed) setCardUnchecked(it.el); });
        updateCartCount();
        updateOrderAttributes();
        updateBulkBar(section);
        bulkBtn.innerHTML = 'Couldn’t add ' + failed.length + ' item' + (failed.length > 1 ? 's' : '') + ' — tap to retry';
        bulkBtn.disabled = false;
        return;
      }
      bulkBtn.innerHTML = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#fff" stroke-width="3"><path d="M5 13l4 4L19 7"/></svg> All added!';
      bulkBtn.classList.add('cs-bulk-btn-done');
      updateCartCount();
      updateOrderAttributes();
      setTimeout(function () {
        bulkBtn.classList.remove('cs-bulk-btn-done');
        bulkBtn.innerHTML = 'Add <span data-cs-bulk-btn-count>' + total + '</span> items to cart';
        bulkBtn.disabled = false;
        updateBulkBar(section);
      }, 2200);
      return;
    }

    bulkBtn.innerHTML = 'Adding ' + (idx + 1) + ' of ' + total + '…';

    var bulkBody = { id: items[idx].id, quantity: 1 };
    var bulkProps = buildLineItemProperties();
    if (PERSONALISATION_ENABLED) bulkProps = mergePersonalisation(bulkProps, items[idx].el);
    if (bulkProps) bulkBody.properties = bulkProps;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(bulkBody),
      credentials: 'same-origin'
    })
    .then(function (r) {
      if (!r.ok) throw new Error('Cart error');
      return r.json();
    })
    .then(function (item) {
      showToast(item.product_title || item.title || items[idx].title, item.variant_title || '');
      addItemsSequentially(items, idx + 1, total, bulkBtn, section);
    })
    .catch(function () {
      if (!retried) {
        // Retry the SAME item once before moving on — a transient error used
        // to skip the item entirely, silently leaving it out of the order.
        bulkBtn.innerHTML = 'Item ' + (idx + 1) + ' failed — retrying…';
        setTimeout(function () {
          addItemsSequentially(items, idx, total, bulkBtn, section, true);
        }, 1200);
        return;
      }
      items[idx].failed = true;
      setTimeout(function () {
        addItemsSequentially(items, idx + 1, total, bulkBtn, section);
      }, 400);
    });
  }

  /* ===================== QUIZ — LEGACY ===================== */
  function initQuizLegacy() {
    var quiz = $('[data-cs-quiz]');
    if (!quiz) return;

    var steps = $$('[data-cs-quiz-step]', quiz);
    if (!steps.length) return;

    var nextBtn = $('[data-cs-quiz-next]', quiz);
    var backBtn = $('[data-cs-quiz-back]', quiz);
    var skipBtn = $('[data-cs-quiz-skip]', quiz);
    var current = 0;
    var selections = { position: 'outfield' };

    function showStep(idx) {
      current = Math.max(0, Math.min(idx, steps.length - 1));
      steps.forEach(function (s, i) { s.style.display = i === current ? '' : 'none'; });

      $$('.cs-quiz-progress i', quiz).forEach(function (dot, i) {
        dot.className = i < current ? 'done' : i === current ? 'active' : '';
      });

      if (backBtn) backBtn.style.display = current === 0 ? 'none' : '';
      if (skipBtn) skipBtn.style.display = current === 0 ? '' : 'none';

      if (nextBtn) {
        var key = steps[current].getAttribute('data-cs-quiz-step');
        nextBtn.textContent = current === steps.length - 1 ? 'Show my kit' : 'Continue';
        nextBtn.disabled = !selections[key];
      }
    }

    quiz.addEventListener('click', function (e) {
      var choice = e.target.closest('[data-cs-choice]');
      if (choice && !choice.closest('a')) {
        var group = choice.getAttribute('data-cs-choice-group');
        var value = choice.getAttribute('data-cs-choice');
        selections[group] = value;
        $$('[data-cs-choice-group="' + group + '"]', quiz).forEach(function (c) {
          c.classList.toggle('sel', c === choice);
        });
        if (nextBtn) nextBtn.disabled = false;
        return;
      }

      if (e.target.closest('[data-cs-quiz-next]')) {
        if (current < steps.length - 1) { showStep(current + 1); }
        else { window.location.href = buildUrl(selections); }
        return;
      }

      if (e.target.closest('[data-cs-quiz-back]')) {
        showStep(current - 1);
        return;
      }

      if (e.target.closest('[data-cs-quiz-skip]')) {
        window.location.href = buildUrl({ player: 'new' });
        return;
      }
    });

    showStep(0);
  }

  /* ===================== QUIZ — DYNAMIC ===================== */
  function initDynamicQuiz() {
    var quiz = $('[data-cs-quiz]');
    if (!quiz) return;

    var foot = $('[data-cs-quiz-foot]', quiz) || $('.cs-quiz-foot', quiz);
    var progressBar = $('[data-cs-quiz-progress]', quiz);
    var nextBtn = $('[data-cs-quiz-next]', quiz);
    var backBtn = $('[data-cs-quiz-back]', quiz);

    var selectedRole = null;
    var selectedPosition = '';
    var selectedTeam = '';
    var nameParts = config.collectPlayerName ? getStoredPlayerParts() : { first: '', last: '', full: '' };
    var selectedSizes = getStoredSizes();

    // The quiz requires BOTH parts (a legacy unsplit `full` value isn't
    // enough — the visible fields would be empty, and Majestri needs the
    // split), so returning visitors from before the split re-type once.
    function nameComplete() { return !!(nameParts.first && nameParts.last); }
    var steps = [];
    var current = 0;

    var roleStep = buildRoleStep();
    var positionStep = buildPositionStep();
    var teamStep = buildTeamStep();
    var sizeStep = buildSizeStep();
    var sizeCats = [];           // discovered size categories (scanned once)
    var sizeStepPopulated = false;
    var nameField = config.collectPlayerName ? buildNameField() : null;
    var nameStep = null; // built lazily when the role has no team step
    quiz.insertBefore(roleStep, foot);
    quiz.insertBefore(teamStep, foot);
    quiz.insertBefore(positionStep, foot);
    quiz.insertBefore(sizeStep, foot);
    teamStep.style.display = 'none';
    positionStep.style.display = 'none';
    sizeStep.style.display = 'none';

    steps = [roleStep];
    updateProgress(1);

    // A single-role club offers no real choice, so the role is picked
    // automatically and the quiz opens on the first step that actually asks
    // something (name / team / position / size). Redirect roles are excluded:
    // auto-selecting one would bounce the visitor off the page on load.
    var autoRole = (config.roles.length === 1 && !config.roles[0].redirectUrl) ? config.roles[0] : null;

    function buildRoleStep() {
      var step = document.createElement('div');
      step.className = 'cs-quiz-step';
      step.setAttribute('data-cs-quiz-step', 'role');
      step.setAttribute('data-cs-quiz-active', '');

      var body = document.createElement('div');
      body.className = 'cs-quiz-body';
      body.innerHTML = '<h2>Who are you shopping for?</h2>' +
        '<p class="cs-quiz-sub">This sets which items are required. You can change it anytime.</p>';

      var grid = document.createElement('div');
      grid.className = 'cs-choice-grid';
      if (config.roles.length === 2) grid.classList.add('cs-choice-two');

      config.roles.forEach(function (role) {
        var btn = document.createElement('button');
        btn.className = 'cs-choice';
        btn.setAttribute('data-cs-choice', role.handle);
        btn.setAttribute('data-cs-choice-group', 'role');
        btn.innerHTML = '<span class="cs-choice-tick">' + TICK_SVG + '</span>' +
          '<span class="cs-choice-label">' + escHtml(role.label) + '</span>';
        grid.appendChild(btn);
      });

      body.appendChild(grid);
      step.appendChild(body);
      return step;
    }

    function buildPositionStep() {
      var step = document.createElement('div');
      step.className = 'cs-quiz-step';
      step.setAttribute('data-cs-quiz-step', 'position');

      var body = document.createElement('div');
      body.className = 'cs-quiz-body';
      body.innerHTML = '<h2>Any specialist position?</h2>' +
        '<p class="cs-quiz-sub">Different positions get different kit.</p>';

      var grid = document.createElement('div');
      grid.className = 'cs-choice-grid cs-choice-two';
      grid.setAttribute('data-cs-position-grid', '');
      body.appendChild(grid);
      step.appendChild(body);
      return step;
    }

    function populatePositionStep(role) {
      var grid = $('[data-cs-position-grid]', positionStep);
      if (!grid) return;
      grid.innerHTML = '';

      if (role.positions.length === 2) {
        grid.classList.add('cs-choice-two');
      } else {
        grid.classList.remove('cs-choice-two');
      }

      role.positions.forEach(function (pos, i) {
        var val = pos.toLowerCase();
        var btn = document.createElement('button');
        btn.className = 'cs-choice';
        if (i === 0) {
          btn.classList.add('sel');
          selectedPosition = val;
        }
        btn.setAttribute('data-cs-choice', val);
        btn.setAttribute('data-cs-choice-group', 'position');
        btn.innerHTML = '<span class="cs-choice-tick">' + TICK_SVG + '</span>' +
          '<span class="cs-choice-label">' + escHtml(pos) + '</span>';
        grid.appendChild(btn);
      });
    }

    function buildTeamStep() {
      var step = document.createElement('div');
      step.className = 'cs-quiz-step';
      step.setAttribute('data-cs-quiz-step', 'team');

      var body = document.createElement('div');
      body.className = 'cs-quiz-body';
      body.innerHTML = '<h2>Which team?</h2>' +
        '<p class="cs-quiz-sub">Pick your team so we show the right gear.</p>';

      var grid = document.createElement('div');
      grid.className = 'cs-choice-grid';
      grid.setAttribute('data-cs-team-grid', '');
      body.appendChild(grid);
      step.appendChild(body);
      return step;
    }

    function populateTeamStep(teams) {
      var grid = $('[data-cs-team-grid]', teamStep);
      if (!grid) return;
      grid.innerHTML = '';

      if (teams.length === 2) {
        grid.classList.add('cs-choice-two');
      } else {
        grid.classList.remove('cs-choice-two');
      }

      teams.forEach(function (t, i) {
        var btn = document.createElement('button');
        btn.className = 'cs-choice';
        if (i === 0) {
          btn.classList.add('sel');
          selectedTeam = t;
        }
        btn.setAttribute('data-cs-choice', t);
        btn.setAttribute('data-cs-choice-group', 'team');
        btn.innerHTML = '<span class="cs-choice-tick">' + TICK_SVG + '</span>' +
          '<span class="cs-choice-label">' + escHtml(teamDisplayName(t)) + '</span>';
        grid.appendChild(btn);
      });
    }

    // Player name fields — collected in the quiz, stored split (first/last),
    // never in the URL. Pinned to the top of the team step when one exists,
    // otherwise shown as its own step (so it's always captured). Two fields
    // rather than one: splitting a combined name afterwards can't be done
    // reliably (double-barrelled surnames, middle names), so the parent
    // decides what goes where.
    function buildNameInput(labelText, part, placeholder, value) {
      var label = document.createElement('label');
      label.className = 'cs-name-label';
      label.innerHTML = escHtml(labelText) + ' <span class="cs-name-req">required</span>';
      var input = document.createElement('input');
      input.type = 'text';
      input.className = 'cs-name-input';
      input.setAttribute('data-cs-player-name', part);
      input.setAttribute('autocomplete', 'off');
      input.placeholder = placeholder;
      input.value = value;
      label.appendChild(input);
      return label;
    }

    function buildNameField() {
      var wrap = document.createElement('div');
      wrap.className = 'cs-name-field';
      var row = document.createElement('div');
      row.className = 'cs-name-row';
      row.appendChild(buildNameInput('Player first name', 'first', 'e.g. James', nameParts.first));
      row.appendChild(buildNameInput('Player last name', 'last', 'e.g. Smith', nameParts.last));
      wrap.appendChild(row);
      return wrap;
    }

    function ensureNameStep() {
      if (nameStep) return nameStep;
      nameStep = document.createElement('div');
      nameStep.className = 'cs-quiz-step';
      nameStep.setAttribute('data-cs-quiz-step', 'name');
      var body = document.createElement('div');
      body.className = 'cs-quiz-body';
      body.innerHTML = '<h2>Who is this kit for?</h2>' +
        '<p class="cs-quiz-sub">We\'ll add this name to each item for fulfilment.</p>';
      nameStep.appendChild(body);
      quiz.insertBefore(nameStep, foot);
      nameStep.style.display = 'none';
      return nameStep;
    }

    function placeNameInTeamStep() {
      if (!nameField) return;
      var body = $('.cs-quiz-body', teamStep);
      var grid = $('[data-cs-team-grid]', teamStep);
      if (body && grid) body.insertBefore(nameField, grid);
    }

    function placeNameInOwnStep() {
      if (!nameField) return;
      var body = $('.cs-quiz-body', ensureNameStep());
      if (body) body.appendChild(nameField);
    }

    // Size step — one selector per size category present in the pool, each
    // offering only sizes that exist. Optional (never blocks the next button).
    function buildSizeStep() {
      var step = document.createElement('div');
      step.className = 'cs-quiz-step';
      step.setAttribute('data-cs-quiz-step', 'sizes');
      var body = document.createElement('div');
      body.className = 'cs-quiz-body';
      body.innerHTML = '<h2>Your sizes</h2>' +
        '<p class="cs-quiz-sub">We\'ll pre-select these on each item — you can still change any of them.</p>' +
        '<div data-cs-sizes-wrap></div>';
      step.appendChild(body);
      return step;
    }

    function populateSizeStep(cats) {
      var wrap = $('[data-cs-sizes-wrap]', sizeStep);
      if (!wrap) return;
      wrap.innerHTML = '';
      cats.forEach(function (c) {
        var group = document.createElement('div');
        group.className = 'cs-size-group';
        var label = document.createElement('span');
        label.className = 'cs-size-group-label';
        label.innerHTML = '<b>' + escHtml(c.category) + '</b> size';
        group.appendChild(label);
        var row = document.createElement('div');
        row.className = 'cs-size-pick-row';
        c.sizes.forEach(function (sz) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'cs-size-pick';
          if (selectedSizes[c.category] && normalizeSize(selectedSizes[c.category]) === normalizeSize(sz)) {
            btn.classList.add('sel');
          }
          btn.setAttribute('data-cs-size-choice', '');
          btn.setAttribute('data-cs-size-cat', c.category);
          btn.setAttribute('data-cs-size-val', sz);
          btn.textContent = sz;
          row.appendChild(btn);
        });
        group.appendChild(row);
        wrap.appendChild(group);
      });
    }

    function rebuildSteps() {
      var newSteps = [roleStep];
      if (selectedRole) {
        var rolePositions = selectedRole.positions || [];
        var teams = scanTeamsFromPool(rolePositions);
        if (teams.length > 0) {
          populateTeamStep(teams);
          if (config.collectPlayerName) placeNameInTeamStep();
          newSteps.push(teamStep);
        } else {
          selectedTeam = '';
          teamStep.style.display = 'none';
          if (config.collectPlayerName) {
            placeNameInOwnStep();
            newSteps.push(nameStep);
          }
        }
        if (roleNeedsPositionStep(selectedRole)) {
          newSteps.push(positionStep);
        }
        if (config.collectSizes) {
          if (sizeCats.length === 0) sizeCats = scanSizeCategoriesFromPool();
          if (sizeCats.length > 0) {
            if (!sizeStepPopulated) { populateSizeStep(sizeCats); sizeStepPopulated = true; }
            newSteps.push(sizeStep);
          }
        }
      }
      // With an auto-selected single role the role screen is skipped — unless
      // it is the only step left, in which case it stays so the quiz still has
      // a "Show my kit" button to navigate with.
      if (autoRole && selectedRole && newSteps.length > 1) newSteps.shift();
      steps = newSteps;
      showStep(current);
    }

    function updateProgress(totalSteps) {
      if (!progressBar) return;
      progressBar.innerHTML = '';
      for (var i = 0; i < totalSteps; i++) {
        var dot = document.createElement('i');
        if (i < current) dot.className = 'done';
        else if (i === current) dot.className = 'active';
        progressBar.appendChild(dot);
      }
    }

    function showStep(idx) {
      current = Math.max(0, Math.min(idx, steps.length - 1));
      steps.forEach(function (s, i) { s.style.display = i === current ? '' : 'none'; });
      // Ensure steps not in the array are hidden
      if (steps.indexOf(roleStep) === -1) roleStep.style.display = 'none';
      if (steps.indexOf(positionStep) === -1) positionStep.style.display = 'none';
      if (steps.indexOf(teamStep) === -1) teamStep.style.display = 'none';
      if (steps.indexOf(sizeStep) === -1) sizeStep.style.display = 'none';
      if (nameStep && steps.indexOf(nameStep) === -1) nameStep.style.display = 'none';
      updateProgress(steps.length);

      if (backBtn) backBtn.style.display = current === 0 ? 'none' : '';
      updateNextBtn();
    }

    // Enable/disable the next button based on the current step's requirements.
    // The team and name steps require a player name when collection is on.
    function updateNextBtn() {
      if (!nextBtn) return;
      nextBtn.textContent = current === steps.length - 1 ? 'Show my kit' : 'Continue';
      var stepKey = steps[current].getAttribute('data-cs-quiz-step');
      var ok = true;
      if (stepKey === 'role') ok = !!selectedRole;
      else if (stepKey === 'position') ok = !!selectedPosition;
      else if (stepKey === 'team') ok = !!selectedTeam && (!config.collectPlayerName || nameComplete());
      else if (stepKey === 'name') ok = !config.collectPlayerName || nameComplete();
      nextBtn.disabled = !ok;
    }

    function onRoleSelected(role) {
      selectedRole = role;
      selectedPosition = '';
      selectedTeam = '';

      if (role.redirectUrl) {
        window.location.href = role.redirectUrl;
        return;
      }

      if (roleNeedsPositionStep(role)) {
        populatePositionStep(role);
      } else {
        selectedPosition = roleEffectivePosition(role, '');
      }

      rebuildSteps();
    }

    quiz.addEventListener('click', function (e) {
      var sizePick = e.target.closest('[data-cs-size-choice]');
      if (sizePick) {
        var sCat = sizePick.getAttribute('data-cs-size-cat');
        var sVal = sizePick.getAttribute('data-cs-size-val');
        selectedSizes[sCat] = sVal;
        $$('[data-cs-size-choice][data-cs-size-cat="' + sCat + '"]', quiz).forEach(function (b) {
          b.classList.toggle('sel', b === sizePick);
        });
        return;
      }

      var choice = e.target.closest('[data-cs-choice]');
      if (choice) {
        var group = choice.getAttribute('data-cs-choice-group');
        var value = choice.getAttribute('data-cs-choice');

        $$('[data-cs-choice-group="' + group + '"]', quiz).forEach(function (c) {
          c.classList.toggle('sel', c === choice);
        });

        if (group === 'role') {
          var role = findRole(value);
          if (role) onRoleSelected(role);
        } else if (group === 'position') {
          selectedPosition = value;
          selectedTeam = '';
          rebuildSteps();
          updateNextBtn();
        } else if (group === 'team') {
          selectedTeam = value;
          updateNextBtn();
        }
        return;
      }

      if (e.target.closest('[data-cs-quiz-next]')) {
        if (nextBtn && nextBtn.disabled) return;
        if (current < steps.length - 1) {
          showStep(current + 1);
        } else {
          var params = { role: selectedRole.handle };
          if (selectedRole.playerType) params.player = selectedRole.playerType;
          if (selectedPosition) params.position = selectedPosition;
          if (selectedTeam) params.team = selectedTeam;
          // Player name never goes in the URL — stash it for the products page.
          if (config.collectPlayerName && nameComplete()) setStoredPlayerParts(nameParts);
          if (Object.keys(selectedSizes).length) setStoredSizes(selectedSizes);
          window.location.href = buildUrl(params);
        }
        return;
      }

      if (e.target.closest('[data-cs-quiz-back]')) {
        showStep(current - 1);
        return;
      }
    });

    // Player name inputs — keep the local first/last values in sync and
    // re-check the next button (both are required to advance past the
    // team/name step).
    quiz.addEventListener('input', function (e) {
      var inp = e.target.closest('[data-cs-player-name]');
      if (!inp) return;
      var part = inp.getAttribute('data-cs-player-name') === 'last' ? 'last' : 'first';
      nameParts[part] = inp.value.trim();
      nameParts.full = ''; // typed parts supersede any legacy unsplit value
      updateNextBtn();
    });

    if (autoRole) {
      var autoBtn = $('[data-cs-choice="' + autoRole.handle + '"]', quiz);
      if (autoBtn) autoBtn.classList.add('sel');
      onRoleSelected(autoRole);
    }

    showStep(0);
  }

  /* ===================== QUIZ ROUTER ===================== */
  function initQuiz() {
    if (config) {
      initDynamicQuiz();
    } else {
      initQuizLegacy();
    }
  }

  /* ===================== SUMMARY BAR POPOVERS ===================== */
  function initSummaryBar() {
    var bar = $('[data-cs-summary]');
    if (!bar) return;
    var openKey = null;

    function closeAll() {
      $$('.cs-pop', bar).forEach(function (p) { p.style.display = 'none'; });
      openKey = null;
    }

    bar.addEventListener('click', function (e) {
      var toggle = e.target.closest('[data-cs-chip-toggle]');
      if (toggle) {
        var key = toggle.getAttribute('data-cs-chip-toggle');
        var pop = $('[data-cs-pop="' + key + '"]', bar);
        if (!pop) return;
        if (openKey === key) { closeAll(); return; }
        closeAll();
        pop.style.display = '';
        openKey = key;
        return;
      }

      var opt = e.target.closest('[data-cs-pop-val]');
      if (opt) {
        var popEl = opt.closest('[data-cs-pop]');
        var paramKey = popEl.getAttribute('data-cs-pop');
        var paramVal = opt.getAttribute('data-cs-pop-val');

        if (config) {
          var params = getParams();
          if (paramKey === 'role') {
            var newRole = findRole(paramVal);
            if (newRole) {
              if (newRole.redirectUrl) {
                window.location.href = newRole.redirectUrl;
                return;
              }
              params.role = paramVal;
              if (newRole.playerType) { params.player = newRole.playerType; }
              else { delete params.player; }
              var newPos = roleEffectivePosition(newRole, '');
              if (newPos) { params.position = newPos; }
              else { delete params.position; }
              if (params.team) {
                var newTeams = scanTeamsFromPool(newRole.positions || []);
                if (newTeams.indexOf(params.team) === -1) delete params.team;
              }
            }
          } else if (paramKey === 'position') {
            params.position = paramVal;
          } else {
            params[paramKey] = paramVal;
          }
          window.location.href = buildUrl(params);
        } else {
          var params2 = getParams();
          params2[paramKey] = paramVal;
          window.location.href = buildUrl(params2);
        }
        return;
      }
    });

    document.addEventListener('click', function (e) {
      if (openKey && !e.target.closest('[data-cs-chip-wrap]')) closeAll();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAll();
    });
  }

  /* ===================== SIZE SELECTION ===================== */
  function initSizeSelection() {
    document.addEventListener('click', function (e) {
      var sizeBtn = e.target.closest('[data-cs-size]');
      if (!sizeBtn) return;
      e.preventDefault();

      var card = sizeBtn.closest('.cs-card');
      if (!card) return;

      $$('[data-cs-size]', card).forEach(function (b) { b.classList.remove('sel'); });
      sizeBtn.classList.add('sel');

      var variantId = sizeBtn.getAttribute('data-variant-id');
      var form = $('[data-cs-card-form]', card);
      if (form && variantId) {
        var hidden = $('[data-cs-variant-id]', form);
        if (hidden) hidden.value = variantId;
      }
    });

    document.addEventListener('change', function (e) {
      var sel = e.target.closest('[data-cs-variant-select]');
      if (!sel) return;
      var form = sel.closest('[data-cs-card-form]');
      if (!form) return;
      var hidden = $('[data-cs-variant-id]', form);
      if (hidden) hidden.value = sel.value;
    });
  }

  /* ===================== AJAX ADD TO CART ===================== */
  function escHtml(str) {
    var d = document.createElement('div');
    d.textContent = str || '';
    return d.innerHTML;
  }

  function showToast(title, variant) {
    var wrap = $('[data-cs-toast-wrap]');
    if (!wrap) return;
    var toast = document.createElement('div');
    toast.className = 'cs-toast';
    toast.innerHTML =
      '<span class="cs-toast-tick"><svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#fff" stroke-width="3"><path d="M5 13l4 4L19 7"/></svg></span>' +
      '<span class="cs-toast-text"><b>Added to cart</b><span>' + escHtml(title) + (variant ? ' · ' + escHtml(variant) : '') + '</span></span>';
    wrap.appendChild(toast);
    setTimeout(function () {
      if (toast.parentNode) toast.parentNode.removeChild(toast);
    }, 3500);
  }

  function updateCartCount() {
    fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        $$('.kpi-cart .count, [data-cart-count], .header__btn-cart .count').forEach(function (el) {
          el.textContent = cart.item_count;
        });
        showViewCartBar(cart.item_count);
      })
      .catch(function () {});
  }

  function showViewCartBar(count) {
    var bar = $('[data-cs-view-cart]');
    if (!bar) return;
    var countEl = $('[data-cs-view-cart-count]', bar);
    if (countEl) countEl.textContent = count;
    bar.style.display = '';
  }

  function initAddToCart() {
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('[data-cs-card-form]');
      if (!form) return;
      e.preventDefault();

      var addBtn = $('[data-cs-add-btn]', form);
      if (!addBtn || addBtn.classList.contains('cs-add-btn-adding')) return;

      var variantInput = $('[data-cs-variant-id]', form);
      if (!variantInput || !variantInput.value) return;

      // Don't let a personalised item ship with an empty required field.
      var productEl = form.closest('[data-cs-product]');
      var missing = firstMissingPersonalisation(productEl);
      if (missing) {
        flagMissingPersonalisation(missing);
        return;
      }

      addBtn.classList.add('cs-add-btn-adding');
      addBtn.innerHTML = 'Adding…';

      var addBody = { id: parseInt(variantInput.value, 10), quantity: 1 };
      var addProps = buildLineItemProperties();
      if (PERSONALISATION_ENABLED) addProps = mergePersonalisation(addProps, productEl);
      if (addProps) addBody.properties = addProps;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(addBody),
        credentials: 'same-origin'
      })
      .then(function (r) {
        if (!r.ok) throw new Error('Cart error');
        return r.json();
      })
      .then(function (item) {
        addBtn.classList.remove('cs-add-btn-adding');
        addBtn.classList.add('cs-add-btn-added');
        addBtn.innerHTML = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="3"><path d="M5 13l4 4L19 7"/></svg> Added';
        showToast(item.product_title || item.title || 'Item', item.variant_title || '');
        updateCartCount();
        updateOrderAttributes();
        setTimeout(function () {
          addBtn.classList.remove('cs-add-btn-added');
          addBtn.innerHTML = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.4"><circle cx="9" cy="20" r="1.4"/><circle cx="18" cy="20" r="1.4"/><path d="M2 3h3l2.5 13h11L21 7H6"/></svg> Add to cart';
        }, 1800);
      })
      .catch(function () {
        addBtn.classList.remove('cs-add-btn-adding');
        addBtn.innerHTML = 'Error — retry';
        setTimeout(function () {
          addBtn.innerHTML = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.4"><circle cx="9" cy="20" r="1.4"/><circle cx="18" cy="20" r="1.4"/><path d="M2 3h3l2.5 13h11L21 7H6"/></svg> Add to cart';
        }, 2000);
      });
    });
  }

  function initViewCartBar() {
    fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cart.item_count > 0) showViewCartBar(cart.item_count);
      })
      .catch(function () {});
  }

  /* ===================== START OVER ===================== */
  // "Start over" returns to the bare collection URL. Clear the stored player
  // name too, so a second child (twins) starts fresh rather than inheriting
  // the previous name.
  function initRestartClear() {
    document.addEventListener('click', function (e) {
      if (e.target.closest('.cs-sum-restart')) {
        clearStoredPlayerName();
        clearStoredSizes();
        storeRemove(RECONCILE_KEY);
      }
    });
  }

  /* ===================== BOOT ===================== */
  function init() {
    initPage();
    initQuiz();
    initSummaryBar();
    initSizeSelection();
    initAddToCart();
    initViewCartBar();
    initRestartClear();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
